import { useDocsDataModelStore, useSearchableTreeList } from '@/models/docs-data/docsData';
import { GlobalClassName } from '@/shared/constant/GlobalClassName';
import { InternalClassName } from '@/shared/constant/InternalClassName';
import { NavigationTypeEnum } from '@/shared/constant/navigation/Navigation';
import clsx from 'clsx';
import React from 'react';
import { useLevelNavbarGroup } from '../docs-layout/components/navbar-content/hooks/useLevelNavbarGroup';
import { NavbarGroup } from '../navbar-group/NavbarGroup';
import { NavbarGroupLevel, NavbarGroupType } from '../navbar-group/type/NavbarGroupTypeLevel';
import { RootBackFolders } from './components/root-back-folders/RootBackFolders';
import SidebarSearch from './components/sidebar-search';
import { SidebarTreeList } from './components/sidebar-tree-list/SidebarTreeList';
import { SidebarTreeNodeScrollAnchor } from './components/sidebar-tree-node-scroll-anchor/SidebarTreeNodeScrollAnchor';
import styles from './SidebarTree.module.css';
import { SidebarTreeStoreProvider } from './store/sidebarTreeStore';

export type SidebarTreeProps = {
  className?: string;
  searchSubfix?: React.ReactNode;
  treeListPrefix?: React.ReactNode;
};

export const SidebarTree = React.memo(function SidebarTreeComponent(props: SidebarTreeProps) {
  const { className, searchSubfix, treeListPrefix } = props;

  const sidebarTreeList = useSearchableTreeList();
  const firstLevelSelectedApiFolderId = useDocsDataModelStore(
    (x) => x.sidebarTree.parentSidebarTreeApiFolderNodes[0]?.id,
  );
  const selectedSidebarTreeNodeKey = useDocsDataModelStore(
    (x) => x.sidebarTree.selectedSidebarTreeNode?.key,
  );

  const initialState = React.useMemo(() => {
    return {
      selectedKey: selectedSidebarTreeNodeKey,
      selectedTime: 0,
    };
  }, [selectedSidebarTreeNodeKey]);

  const navigation = useDocsDataModelStore((x) => x.navigation);
  const foldNavbarFolderItem = React.useMemo(() => {
    if (navigation.type === NavigationTypeEnum.None) {
      return navigation.foldNavbarFolderItem;
    }
    return undefined;
  }, [navigation]);

  const { firstLevelNavbarGroup, secondLevelNavbarGroup, thirdLevelNavbarGroup } =
    useLevelNavbarGroup(navigation);

  const hasLevelNavbarGroup = React.useMemo(
    () => firstLevelNavbarGroup || secondLevelNavbarGroup || thirdLevelNavbarGroup,
    [firstLevelNavbarGroup, secondLevelNavbarGroup, thirdLevelNavbarGroup],
  );
  return (
    <div
      className={clsx(GlobalClassName.Sidebar, 'flex w-full flex-col overflow-y-auto', className)}
    >
      <SidebarTreeStoreProvider state={initialState}>
        <SidebarTreeNodeScrollAnchor />
        <div
          className={clsx('px-2 os:px-5', {
            'mt-2.5': !hasLevelNavbarGroup,
            'mt-5': hasLevelNavbarGroup,
          })}
        >
          {!!foldNavbarFolderItem?.length && (
            <div className="mb-2">
              <NavbarGroup
                items={foldNavbarFolderItem}
                level={NavbarGroupLevel.Level2}
                type={NavbarGroupType.Select}
                className={clsx('min-w-full', styles['fold-navbar-folder-item'])}
              />
            </div>
          )}

          <SidebarSearch />
          {searchSubfix}
        </div>

        <div
          className={clsx(
            GlobalClassName.SidebarMenu,
            InternalClassName.TreeScrollContainer,
            'relative h-full w-full overflow-y-auto px-2 pb-2 pt-5 os:px-5 os:pb-10',
          )}
        >
          {treeListPrefix}
          <RootBackFolders />
          <SidebarTreeList
            list={sidebarTreeList}
            className="w-full"
            indentation={0}
            firstLevelSelectedApiFolderId={firstLevelSelectedApiFolderId}
            isHidden={false}
          />
        </div>
      </SidebarTreeStoreProvider>
    </div>
  );
});
